import { z } from 'zod'
import { optionalText, versionSchema } from './common.js'
import type { TreasuryKind } from './finance.js'
import { type PaymentMethod, type PaymentMethodRef, paymentMethodSchema } from './sales.js'

/**
 * The company's own payment methods (ADR-047): Visa, Mastercard, Thawani, Bank Muscat transfer. Each is a
 * name over one of the five types (`PaymentMethod`), which keeps the accounting rules: what account the
 * money can move through, how the shift report counts it. A method may start on its own account and may be
 * offered at the till (any type but cash: cash goes into the drawer). Archived, never deleted: documents
 * keep it.
 */

const base = z.object({
  name: z.string().trim().min(1, 'Name the payment method').max(60),
  nameAr: optionalText(60),
  kind: paymentMethodSchema,
  /** The cash or bank account it starts on in forms (and takes at the till); null: the type's default. */
  treasuryAccountId: z
    .uuid()
    .transform((v) => v.toLowerCase())
    .nullish()
    .transform((v) => v ?? null),
  /** Offered as a tender at the till. */
  atTill: z.boolean(),
})

const notCashAtTill = (v: { kind: PaymentMethod; atTill: boolean }) => !(v.atTill && v.kind === 'CASH')
const tillRule = { path: ['atTill'], message: 'Cash at the till goes into the drawer' }

export const companyPaymentMethodInputSchema = base.refine(notCashAtTill, tillRule)
export type CompanyPaymentMethodInput = z.input<typeof companyPaymentMethodInputSchema>

export const companyPaymentMethodUpdateSchema = base
  .extend({ version: versionSchema })
  .refine(notCashAtTill, tillRule)
export type CompanyPaymentMethodUpdate = z.input<typeof companyPaymentMethodUpdateSchema>

export const companyPaymentMethodListQuerySchema = z.object({
  /** Include archived methods (the settings screen); forms list the active ones only. */
  includeArchived: z.stringbool().optional(),
})

/** `GET /payment-methods`: the company's methods, by type then name. */
export interface CompanyPaymentMethod extends PaymentMethodRef {
  kind: PaymentMethod
  treasuryAccount: { id: string; name: string; nameAr: string | null; kind: TreasuryKind } | null
  atTill: boolean
  archived: boolean
  /** Documents recorded with it (receipts, refunds, supplier payments and refunds, collections). */
  used: number
  version: number
}

/**
 * A form's method choice in one string: a plain type (`CASH`) or a company method (`pm:<id>`), so one
 * select lists both. `method` is what the document records as its type.
 */
export type MethodChoice = PaymentMethod | `pm:${string}`

export function methodChoice(
  method: PaymentMethod,
  paymentMethodId: string | null | undefined,
): MethodChoice {
  return paymentMethodId ? `pm:${paymentMethodId}` : method
}

/** The document fields of a choice; a company method's type comes from the list (null: not in it). */
export function fromMethodChoice(
  choice: MethodChoice,
  methods: readonly Pick<CompanyPaymentMethod, 'id' | 'kind'>[],
): { method: PaymentMethod; paymentMethodId: string | null } | null {
  if (!choice.startsWith('pm:')) return { method: choice as PaymentMethod, paymentMethodId: null }
  const found = methods.find((m) => m.id === choice.slice(3))
  return found ? { method: found.kind, paymentMethodId: found.id } : null
}
